import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Form, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { resetError } from "../../store/reducerSlice/admin";
import { resendMfa, verifyMfa } from "../../store/actions/adminAction";
import ApiResponsePopup from "../common/modal/ApiResponsePopup";
import provider from "../../assets/images/product.png";

const MAX_MFA_ATTEMPTS = 5;
const ATTEMPT_LOCKOUT_SECONDS = 60;
const RESEND_COOLDOWN_SECONDS = 60;
const MFA_RESEND_COOLDOWN_END_KEY = "mfaResendCooldownEndAt";

const getCountdownLabel = (seconds) => {
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const secs = (seconds % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
};

const LoginMfaForm = () => {
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isShow, setIsShow] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [attemptLockSeconds, setAttemptLockSeconds] = useState(0);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [modalMessage, setModalMessage] = useState("");
  const hasReachedMaxAttempts = failedAttempts >= MAX_MFA_ATTEMPTS;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isAdminLoggedIn, adminDetail, error: apiError } = useSelector(
    (state) => state.admin
  );

  // Guard: allow only when navigated from login; redirect on refresh or direct URL
  useEffect(() => {
    const fromLogin = sessionStorage.getItem("mfaFromLogin");

    if (fromLogin) {
      // Consume the one-time flag immediately
      sessionStorage.removeItem("mfaFromLogin");
      return; // legitimate Login → MFA flow — allow
    }

    // Flag missing = hard refresh (already consumed) or direct URL access
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace("/login");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAdminLoggedIn && adminDetail) {
      navigate("/");
    }

    return () => {
      dispatch(resetError());
    };
  }, [isAdminLoggedIn, adminDetail, navigate, dispatch]);

  useEffect(() => {
    const pendingMfaLogin = sessionStorage.getItem("pendingMfaLogin");
    if (!pendingMfaLogin) {
      return;
    }

    const now = Date.now();
    const storedEndAt = Number(
      sessionStorage.getItem(MFA_RESEND_COOLDOWN_END_KEY)
    );

    if (storedEndAt && storedEndAt > now) {
      const remainingSeconds = Math.ceil((storedEndAt - now) / 1000);
      setResendCooldownSeconds(remainingSeconds);
      return;
    }

    const newEndAt = now + RESEND_COOLDOWN_SECONDS * 1000;
    sessionStorage.setItem(MFA_RESEND_COOLDOWN_END_KEY, String(newEndAt));
    setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
  }, []);

  useEffect(() => {
    if (!attemptLockSeconds) {
      return;
    }

    // During security lockout, stop and clear resend cooldown state.
    setResendCooldownSeconds(0);
    sessionStorage.removeItem(MFA_RESEND_COOLDOWN_END_KEY);

    const intervalId = setInterval(() => {
      setAttemptLockSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId);
          setFailedAttempts(0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [attemptLockSeconds]);

  useEffect(() => {
    if (!resendCooldownSeconds) {
      sessionStorage.removeItem(MFA_RESEND_COOLDOWN_END_KEY);
      return;
    }

    const intervalId = setInterval(() => {
      setResendCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, [resendCooldownSeconds]);

  const markVerificationFailure = (message) => {
    setModalMessage(message);
    setIsShow(true);

    setFailedAttempts((prevAttempts) => {
      const updatedAttempts = prevAttempts + 1;

      if (updatedAttempts >= MAX_MFA_ATTEMPTS) {
        setAttemptLockSeconds(ATTEMPT_LOCKOUT_SECONDS);
        setOtpError(
          "Too many incorrect MFA attempts. Your account may be marked inactive."
        );
      }

      return updatedAttempts;
    });
  };

  const submitHandler = (e) => {
    e.preventDefault();

    if (attemptLockSeconds > 0) {
      setOtpError(
        `Too many incorrect attempts.`
      );
      return;
    }

    const trimmedOtp = otp.trim();
    if (!trimmedOtp) {
      setOtpError("OTP is required");
      return;
    }

    if (!/^\d{6}$/.test(trimmedOtp)) {
      setOtpError("Please enter a valid 6-digit OTP");
      return;
    }

    const pendingMfaLogin = sessionStorage.getItem("pendingMfaLogin");
    if (!pendingMfaLogin) {
      setIsShow(true);
      return;
    }

    let loginPayload = null;
    try {
      loginPayload = JSON.parse(pendingMfaLogin);
    } catch (e) {
      setIsShow(true);
      return;
    }

    if (!loginPayload?.clientEmail) {
      setIsShow(true);
      return;
    }

    setOtpError("");
    setIsSubmitting(true);

    dispatch(
      verifyMfa({
        clientEmail: loginPayload.clientEmail,
        otpCode: trimmedOtp,
      })
    ).then((result) => {
      const loginRes = result?.payload;

      if (!loginRes?.success) {
        markVerificationFailure(
          loginRes?.message ||
            "MFA verification failed. Please check the code and try again."
        );
        setIsSubmitting(false);
        return;
      }

      const token = loginRes?.data?.token;
      const mfaRequired = !!loginRes?.data?.mfaRequired;

      if (mfaRequired || !token) {
        markVerificationFailure(
          "MFA verification could not be completed. Please enter the latest code and try again."
        );
        setIsSubmitting(false);
        return;
      }

      setFailedAttempts(0);
      sessionStorage.removeItem("pendingMfaLogin");
      navigate("/");
      setIsSubmitting(false);
    });
  };

  const resendHandler = () => {
    if (
      hasReachedMaxAttempts ||
      attemptLockSeconds > 0 ||
      resendCooldownSeconds > 0 ||
      isResending
    ) {
      return;
    }

    const pendingMfaLogin = sessionStorage.getItem("pendingMfaLogin");
    if (!pendingMfaLogin) {
      setModalMessage(
        "Your MFA session has expired. Please go back to login and authenticate again."
      );
      setIsShow(true);
      return;
    }

    let loginPayload = null;
    try {
      loginPayload = JSON.parse(pendingMfaLogin);
    } catch (e) {
      setModalMessage(
        "Your MFA session is invalid. Please go back to login and try again."
      );
      setIsShow(true);
      return;
    }

    if (!loginPayload?.clientEmail) {
      setModalMessage(
        "Your MFA session is missing required details. Please go back to login."
      );
      setIsShow(true);
      return;
    }

    setIsResending(true);

    dispatch(
      resendMfa({
        clientEmail: loginPayload.clientEmail,
      })
    ).then((result) => {
      const resendRes = result?.payload;

      if (!resendRes?.success) {
        setModalMessage(
          resendRes?.message ||
            "Unable to resend the MFA code right now. Please try again in a moment."
        );
        setIsShow(true);
        setIsResending(false);
        return;
      }

      setModalMessage(
        "A new MFA code has been sent to your registered email address."
      );
      setIsShow(true);
      setOtp("");
      setOtpError("");
      setFailedAttempts(0);
      const resendEndAt = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
      sessionStorage.setItem(
        MFA_RESEND_COOLDOWN_END_KEY,
        String(resendEndAt)
      );
      setResendCooldownSeconds(RESEND_COOLDOWN_SECONDS);
      setIsResending(false);
    });
  };

  const hideHandlerRes = () => {
    setIsShow(false);
  };


  return (
    <Form onSubmit={submitHandler} className="loginFormSection" noValidate>
      <h4 className="loginLongTitle">
        Your account is protected with multi-factor authentication (MFA)
      </h4>
      <p className="mb-2 text-muted" style={{ fontSize: "13px" }}>
        For security reasons, five consecutive incorrect MFA entries may cause
        your account to be marked inactive.
      </p>
     

      <Form.Group className="mb-4 mt-4" controlId="formOtp">
        <Form.Label>
          MFA Code <span className="required">*</span>
        </Form.Label>
        <Form.Control
          className="py-2 input-form"
          type="text"
          name="otp"
          placeholder="Enter MFA Code"
          value={otp}
          onChange={(e) => {
            const sanitizedOtp = e.target.value.replace(/\D/g, "").slice(0, 6);
            setOtp(sanitizedOtp);
            if (otpError) {
              setOtpError("");
            }
          }}
          maxLength={6}
          inputMode="numeric"
          autoComplete="one-time-code"
          isInvalid={!!otpError}
        />
        <Form.Control.Feedback type="invalid">{otpError}</Form.Control.Feedback>
       {/* {failedAttempts > 0 && attemptLockSeconds === 0 && (
          <Form.Text className="text-danger">
            {`Incorrect attempts: ${failedAttempts}/${MAX_MFA_ATTEMPTS}. After ${MAX_MFA_ATTEMPTS} failed attempts, your account may be marked inactive.`}
          </Form.Text>
        )}
        {attemptLockSeconds > 0 && (
          <Form.Text className="text-danger d-block mt-1">
            {`Temporary security timeout active. Please wait ${getCountdownLabel(
              attemptLockSeconds
            )} before retrying.`}
          </Form.Text>
        )}}
        {/* {resendCooldownSeconds > 0 && (
          <Form.Text className="text-muted d-block mt-1">
            {`OTP received. Reset MFA code will be available in ${getCountdownLabel(
              resendCooldownSeconds
            )}.`}
          </Form.Text>
        )} */}
      </Form.Group>

      <Button
        className="btn-primary mt-2 border-0 mb-2"
        style={{
          fontSize: "14px",
          height: "48px",
          filter: "drop-shadow()",
          boxShadow: "1px 4px 8px 1px rgba(133, 93, 217, 0.5)",
        }}
        type="submit"
        disabled={isSubmitting || attemptLockSeconds > 0}
      >
        {isSubmitting
          ? "Verifying..."
          // : attemptLockSeconds > 0
          // ? `Retry in ${getCountdownLabel(attemptLockSeconds)}`
          : "Submit OTP"}
      </Button>

      {!hasReachedMaxAttempts && attemptLockSeconds === 0 && (
        <Form.Group className="mb-2 text-center">
          {resendCooldownSeconds === 0 ? (
            <Button
              variant="link"
              className="p-0"
              onClick={resendHandler}
              disabled={isResending}
            >
              {isResending ? "Resetting..." : "Reset MFA code"}
            </Button>
          ) : (
            <Form.Text className="text-muted">
              {`Reset MFA code button will be enabled in ${getCountdownLabel(
                resendCooldownSeconds
              )}.`}
            </Form.Text>
          )}
        </Form.Group>
      )}

      <Form.Group className="mb-2 text-center">
        <Link to="/login">
          Back to login
        </Link>
      </Form.Group>

      <Form.Group className="text-center ms-2 ">Powered by</Form.Group>
      <div className="text-center">
        <img style={{ width: "20%" }} src={provider} alt="" />
      </div>

      <ApiResponsePopup
        {...{
          hideHandlerRes,
          isRecordUpdated: isShow,
          title: "OTP Verification",
          errorRes: modalMessage ||
            apiError?.message ||
            "OTP verification failed. Please try again.",
        }}
      />
    </Form>
  );
};

export default LoginMfaForm;
